import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import 'highlight.js/styles/github-dark.css';

function isSafeHref(href: string | undefined): boolean {
  if (!href) return false;
  const t = href.trim().toLowerCase();
  return !t.startsWith('javascript:') && !t.startsWith('data:');
}

function extractText(node: React.ReactNode): string {
  if (node == null || typeof node === 'boolean') return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(extractText).join('');
  if (React.isValidElement(node)) return extractText((node as React.ReactElement<any>).props.children);
  return '';
}

const CodeBlock: React.FC<{ language: string; text: string; children: React.ReactNode }> = ({
  language,
  text,
  children,
}) => {
  const [copied, setCopied] = useState<'idle' | 'done' | 'fail'>('idle');
  const flash = (state: 'done' | 'fail') => {
    setCopied(state);
    window.setTimeout(() => setCopied('idle'), 1500);
  };
  const onCopy = () => {
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text).then(() => flash('done')).catch(() => flash('fail'));
      return;
    }
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    try {
      document.execCommand('copy');
      flash('done');
    } catch {
      flash('fail');
    } finally {
      document.body.removeChild(ta);
    }
  };

  const label = copied === 'done' ? 'Copied' : copied === 'fail' ? 'Failed' : 'Copy';

  return (
    <div className="md-code-wrap">
      <div className="md-code__toolbar">
        <span className={`md-code__lang${language ? '' : ' md-code__lang--empty'}`}>{language}</span>
        <button
          type="button"
          className={`md-code__copy${copied === 'done' ? ' md-code__copy--done' : ''}`}
          aria-label="Copy code"
          onClick={onCopy}
        >
          {label}
        </button>
      </div>
      <pre className="md-code">{children}</pre>
    </div>
  );
};

const components: React.ComponentProps<typeof ReactMarkdown>['components'] = {
  pre({ children }) {
    const codeEl = React.Children.toArray(children).find(
      (c): c is React.ReactElement<any> =>
        React.isValidElement(c) && (c.type === 'code' || ((c as React.ReactElement<any>).props as any)?.className?.includes?.('language-')),
    );
    if (!codeEl) return <pre>{children}</pre>;
    const codeClass: string = codeEl.props.className ?? '';
    const lang = (/language-(\w+)/.exec(codeClass) ?? [])[1] ?? '';
    const text = extractText(codeEl.props.children).replace(/\n$/, '');
    return (
      <CodeBlock language={lang} text={text}>
        <code className={codeClass}>{codeEl.props.children}</code>
      </CodeBlock>
    );
  },
  code({ className, children, ...rest }) {
    // react-markdown calls `code` for both block and inline. Block code goes
    // through `pre` above; for inline code we render a minimal tag here.
    return (
      <code className={`md-inline-code ${className ?? ''}`.trim()} {...rest}>
        {children}
      </code>
    );
  },
  a({ href, title, children }) {
    if (!isSafeHref(href)) return <>{children}</>;
    return (
      <a href={href} title={title} target="_blank" rel="noopener noreferrer">
        {children}
      </a>
    );
  },
};

// Memoised — remark + rehype-highlight are the expensive part of a thread render.
export const MarkdownContent = React.memo(function MarkdownContent({
  source,
  className,
}: {
  source: string;
  className?: string;
}) {
  return (
    <div className={className}>
      <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeHighlight]} components={components}>
        {source}
      </ReactMarkdown>
    </div>
  );
});
